/**
 * WebSocketEventListener
 * 
 * Listens for WebSocket connection events and triggers alerts when connection status changes.
 * Handles events like:
 * - Websocket.Connected
 * - Websocket.Disconnected
 * - Server.Connected
 * - Server.Disconnected
 * 
 * @module WebSocketEventListener
 */

import BaseEventListener from './BaseEventListener';
import { AlertType, GlobalEvents } from '../../models/AlertModels';

/**
 * Logger for WebSocketEventListener
 */
const Logger = {
  log: (...args) => console.log('[WebSocketEventListener]', ...args),
  warn: (...args) => console.warn('[WebSocketEventListener]', ...args),
  error: (...args) => console.error('[WebSocketEventListener]', ...args),
  debug: (...args) => console.debug('[WebSocketEventListener]', ...args)
};

/**
 * Event names for WebSocket events
 * @readonly
 * @enum {string}
 */
const WS_EVENTS = {
  CONNECTED: 'websocket-connected',
  DISCONNECTED: 'websocket-disconnected',
  SERVER_CONNECTED: 'server-connected',
  SERVER_DISCONNECTED: 'server-disconnected'
};

/**
 * Listener for WebSocket connection events
 * @class WebSocketEventListener
 * @extends BaseEventListener
 */
class WebSocketEventListener extends BaseEventListener {
  /**
   * Creates an instance of WebSocketEventListener
   * @param {Object} alertService - Service for alert operations
   */
  constructor(alertService) {
    super(alertService, 'WebSocket');
    
    // Bind event handlers
    this._handleWebSocketConnected = this._handleWebSocketConnected.bind(this);
    this._handleWebSocketDisconnected = this._handleWebSocketDisconnected.bind(this);
    this._handleServerConnected = this._handleServerConnected.bind(this);
    this._handleServerDisconnected = this._handleServerDisconnected.bind(this);
    

  }

  /**
   * Set up event listeners for WebSocket events
   * @protected
   * @returns {Promise<void>}
   */
  async _setupEventListeners() {
    // Add event listeners for WebSocket events
    window.addEventListener(WS_EVENTS.CONNECTED, this._handleWebSocketConnected);
    window.addEventListener(WS_EVENTS.DISCONNECTED, this._handleWebSocketDisconnected);
    window.addEventListener(WS_EVENTS.SERVER_CONNECTED, this._handleServerConnected);
    window.addEventListener(WS_EVENTS.SERVER_DISCONNECTED, this._handleServerDisconnected);
    

  }

  /**
   * Remove event listeners for WebSocket events
   * @protected
   * @returns {Promise<void>}
   */
  async _removeEventListeners() {
    // Remove event listeners for WebSocket events
    window.removeEventListener(WS_EVENTS.CONNECTED, this._handleWebSocketConnected);
    window.removeEventListener(WS_EVENTS.DISCONNECTED, this._handleWebSocketDisconnected);
    window.removeEventListener(WS_EVENTS.SERVER_CONNECTED, this._handleServerConnected);
    window.removeEventListener(WS_EVENTS.SERVER_DISCONNECTED, this._handleServerDisconnected);
    

  }

  /**
   * Handle WebSocket connected event
   * @private
   * @param {Event} event - The event object
   */
  async _handleWebSocketConnected(event) {

    
    const eventData = {
      'Websocket.Connected': true, // Key for RuleEvaluator
      timestamp: Date.now(),
      details: {
        rawEventDetails: event.detail || {}
      },
      event: 'Websocket.Connected' // Standard event name property
    };

    await this.handleEvent(eventData);
  }

  /**
   * Handle WebSocket disconnected event
   * @private
   * @param {Event} event - The event object
   */
  async _handleWebSocketDisconnected(event) {

    
    // Create a more complete event data object with properties needed for notification creation
    const eventData = {
      // Standard event property for rule evaluation
      'Websocket.Disconnected': true,
      
      // Event metadata
      timestamp: Date.now(),
      eventTime: new Date().toISOString(),
      
      // Event details
      details: {
        rawEventDetails: event.detail || {},
        reason: (event.detail && event.detail.reason) || 'Connection lost',
        code: (event.detail && event.detail.code) || 1006
      },
      
      // Standard event type for rule matching
      event: 'Websocket.Disconnected',
      
      // Additional fields for notification content
      type: 'Disconnect',
      name: 'WebSocket Disconnected',
      description: `WebSocket connection was lost. ${(event.detail && event.detail.reason) || ''}`
    };
    

    await this.handleEvent(eventData);
  }

  /**
   * Handle server connected event
   * @private
   * @param {Event} event - The event object
   */
  async _handleServerConnected(event) {

    
    const eventData = {
      'Server.Connected': true, // Key for RuleEvaluator
      timestamp: Date.now(),
      details: {
        rawEventDetails: event.detail || {}
      },
      event: 'Server.Connected' // Standard event name property
    };

    await this.handleEvent(eventData);
  }

  /**
   * Handle server disconnected event
   * @private
   * @param {Event} event - The event object
   */
  async _handleServerDisconnected(event) {

    
    const eventData = {
      'Server.Disconnected': true, // Key for RuleEvaluator
      timestamp: Date.now(),
      details: {
        rawEventDetails: event.detail || {}
      },
      event: 'Server.Disconnected' // Standard event name property
    };

    await this.handleEvent(eventData);
  }

  /**
   * Handle an event by evaluating alerts and creating notifications
   * @param {Object} eventData - The event data
   * @returns {Promise<boolean>} True if the event was handled successfully
   */
  async handleEvent(eventData) {
    // Call the parent method to track statistics
    await super.handleEvent(eventData);
    
    if (!this.active) {
      return false;
    }
    
    try {
      // Use the updated AlertService.evaluateEvent method which handles notification creation
      const { triggeredAlerts, notifications } = await this.alertService.evaluateEvent(
        eventData,
        AlertType.GLOBAL
        // No serverId for global events
      );
      
      // Log triggered alerts
      if (triggeredAlerts.length > 0) {

        // No need to log each alert individually
      }
      
      // Log created notifications
      if (notifications.length > 0) {

      }
      
      return true;
    } catch (error) {
      Logger.error('Error handling WebSocket event', error);
      return false;
    }
  }
}

export default WebSocketEventListener;
